'use client';

import { AppImageClient } from '@components/appImage';
import { Card } from '@components/card';
import { CTAButtons } from '@components/ctaButtons';
import { Tooltiped } from '@components/tooltiped';
import { Typography } from '@components/typography';
import { classNames } from '@lib/classNames/classNames';
import { memo } from 'react';

import { PrimeCardSectionProps } from '../types';

import { useIsMobile } from '@/shared/hooks';

const DEFAULT_CARD_HEIGHT = { lg: 348, sm: 'auto' as const };
const DEFAULTTITLE_MAX_WIDTH = 822;

export const PrimeCard = memo(
  ({
    title,
    titleId,
    description,
    descriptionId,
    titleMaxWidth,
    descriptionMaxWidth,
    mainButton,
    secondaryButton,
    chips,
    image,
    imageSm,
    contentWrapperClassName,
    cardClassName,
    cardHeight = DEFAULT_CARD_HEIGHT,
  }: PrimeCardSectionProps) => {
    const isMobile = useIsMobile();

    /** minHeight вместо height: длинные локали (kz) не должны прижимать текст к кнопкам */
    const CARD_WRAPPER_STYLES = {
      minHeight: isMobile
        ? (cardHeight.sm ?? 'auto')
        : (cardHeight.lg ?? 'auto'),
    };

    const dynamicTitleWidth = {
      maxWidth: titleMaxWidth ?? DEFAULTTITLE_MAX_WIDTH,
    };
    const dynamicDescriptionWidth = {
      maxWidth: descriptionMaxWidth ?? DEFAULTTITLE_MAX_WIDTH,
    };

    return (
      <Card
        variant="primary"
        rounded={isMobile ? '20' : '28'}
        className={cardClassName}
      >
        <div
          style={CARD_WRAPPER_STYLES}
          className={classNames({
            cls: 'relative flex flex-col justify-between gap-6 overflow-hidden p-10 max-sm:p-5',
            additional: [contentWrapperClassName],
          })}
        >
          <div className="flex flex-col gap-6 max-sm:flex-col-reverse max-sm:gap-5">
            {chips && (
              <div className="flex flex-wrap gap-4">
                {chips.map((chip) =>
                  chip.tooltip ? (
                    <Tooltiped
                      key={chip.id}
                      variant="light"
                      position="bottom-left"
                      tooltipContent={<>{chip.tooltip}</>}
                      className="bg-stroke-primary relative rounded-2xl px-6 py-3"
                    >
                      {chip.label}
                      <span className="border-custom-base-inverse-primary bg-stroke-primary absolute top-[-5px] left-[-5px] flex h-5 w-5 items-center justify-center rounded-full border-4 p-2 text-[13px]">
                        i
                      </span>
                    </Tooltiped>
                  ) : (
                    <div
                      key={chip.id}
                      className="bg-stroke-primary rounded-2xl px-6 py-3"
                    >
                      {chip.label}
                    </div>
                  ),
                )}
              </div>
            )}
            <div className="flex flex-col gap-[20px] max-sm:gap-[12px]">
              <Typography
                id={titleId}
                variant="heading-l"
                style={dynamicTitleWidth}
              >
                {title}
              </Typography>
              {description && (
                <Typography
                  id={descriptionId}
                  variant="text-m"
                  style={dynamicDescriptionWidth}
                >
                  {description}
                </Typography>
              )}
            </div>
          </div>

          <div className="z-1 flex flex-row-reverse justify-end gap-4 max-sm:flex-col-reverse">
            <CTAButtons
              mainButton={mainButton}
              secondaryButton={secondaryButton}
            />
          </div>

          {/** Web makaronina image */}
          {image && (
            <AppImageClient
              {...image.data}
              width={image.width}
              height={image.height}
              alt={image.alt ?? ''}
              className={classNames({
                cls: image.className,
                additional: [
                  'pointer-events-none rounded-[28px] max-sm:hidden',
                ],
              })}
            />
          )}

          {/** Mobile makaronina image */}
          {imageSm && (
            <AppImageClient
              {...imageSm.data}
              width={imageSm.width}
              height={imageSm.height}
              alt={imageSm.alt ?? ''}
              className={classNames({
                cls: imageSm.className,
                additional: ['rounded-20 pointer-events-none md:hidden'],
              })}
            />
          )}
        </div>
      </Card>
    );
  },
);
